import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface AttendanceRank {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  attendanceCount: number;
}

interface Exercise {
  id: string;
  name: string;
  description?: string;
}

interface PersonalRecordRank {
  id: string;
  weight: number;
  reps: number;
  date: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export class RankingsPage implements View {
  private activeTab: 'attendance' | 'prs' = 'attendance';
  private attendanceRankings: AttendanceRank[] = [];
  private exercises: Exercise[] = [];
  private selectedExerciseId: string = '';
  private prRankings: PersonalRecordRank[] = [];
  private loading = true;
  private error: string = '';

  async render(): Promise<string> {
    const params = router.getParams();
    if (params.tab === 'prs') {
      this.activeTab = 'prs';
    } else {
      this.activeTab = 'attendance';
    }
    if (params.exerciseId) {
      this.selectedExerciseId = params.exerciseId;
    }

    await this.initData();

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 style="display:flex;align-items:center;gap:0.6rem;">
            ${getTyrisIcon('rankings', { size: 28 })} Récords y Rankings
          </h2>
          <p>Mídete con la comunidad de Tyris y supera tus metas.</p>
        </div>
      </div>

      <!-- Pestañas de Navegación -->
      <div style="display: flex; gap: 1rem; margin-bottom: 1.5rem; border-bottom: 1px solid var(--border-metallic); padding-bottom: 1rem;">
        <button id="tab-btn-attendance" class="btn ${this.activeTab === 'attendance' ? 'btn-primary' : 'btn-outline'}" data-testid="tab-attendance" style="display:inline-flex;align-items:center;gap:0.5rem;">
          ${getTyrisIcon('asistencia', { size: 18 })} Asistencia
        </button>
        <button id="tab-btn-prs" class="btn ${this.activeTab === 'prs' ? 'btn-primary' : 'btn-outline'}" data-testid="tab-prs" style="display:inline-flex;align-items:center;gap:0.5rem;">
          ${getTyrisIcon('mis-records', { size: 18 })} Récords de Fuerza
        </button>
      </div>

      <!-- Mensajes de Carga o Error -->
      ${this.loading ? `
        <div class="admin-state-loading" style="padding: 3rem; text-align: center;">
          <div style="color: var(--tyris-energy); animation: spin 1s linear infinite; display: inline-block;">
            ${getTyrisIcon('en-curso', { size: 36 })}
          </div>
          <p style="margin-top: 1rem; color: var(--text-muted);">Cargando clasificaciones...</p>
        </div>
      ` : ''}

      ${this.error ? `
        <div class="card-glass admin-state-error" style="padding: 2rem; text-align: center; border-color: rgba(220,53,69,0.2);">
          <div style="color: var(--color-error); margin-bottom: 0.5rem;">
            ${getTyrisIcon('alerta', { size: 40 })}
          </div>
          <h3 style="margin-top: 0.5rem; color: var(--text-main);">Error al cargar clasificaciones</h3>
          <p style="color: var(--text-muted);">${this.error}</p>
        </div>
      ` : ''}

      ${!this.loading && !this.error ? this.renderActiveTabContent() : ''}
    `;
  }

  afterRender(): void {
    const tabAttendance = document.getElementById('tab-btn-attendance');
    const tabPrs = document.getElementById('tab-btn-prs');

    if (tabAttendance) {
      tabAttendance.addEventListener('click', () => {
        window.location.hash = '#/rankings?tab=attendance';
      });
    }

    if (tabPrs) {
      tabPrs.addEventListener('click', () => {
        window.location.hash = '#/rankings?tab=prs';
      });
    }

    const selectExercise = document.getElementById('select-exercise-ranking') as HTMLSelectElement | null;
    if (selectExercise) {
      selectExercise.addEventListener('change', (e) => {
        const val = (e.target as HTMLSelectElement).value;
        if (val) {
          window.location.hash = `#/rankings?tab=prs&exerciseId=${val}`;
        } else {
          window.location.hash = '#/rankings?tab=prs';
        }
      });
    }
  }

  private async initData() {
    if (!this.loading) return;

    try {
      if (this.activeTab === 'attendance' && this.attendanceRankings.length === 0) {
        const response = await apiClient.get<any>('/api/attendance/ranking');
        this.attendanceRankings = Array.isArray(response) ? response : (response?.data ?? []);
      } else if (this.activeTab === 'prs') {
        if (this.exercises.length === 0) {
          this.exercises = await apiClient.get<Exercise[]>('/api/exercises');
        }
        if (this.exercises.length > 0 && !this.selectedExerciseId) {
          this.selectedExerciseId = this.exercises[0].id;
        }
        if (this.selectedExerciseId && this.prRankings.length === 0) {
          const response = await apiClient.get<any>(`/api/records/ranking/${this.selectedExerciseId}`);
          this.prRankings = Array.isArray(response) ? response : (response?.data ?? []);
        }
      }
      this.error = '';
    } catch (err: any) {
      console.error(err);
      this.error = err.message || 'Error de conexión con la API.';
    } finally {
      this.loading = false;
    }
  }

  private renderActiveTabContent(): string {
    if (this.activeTab === 'attendance') {
      return this.renderAttendanceTable();
    } else {
      return this.renderPrsContent();
    }
  }

  private renderAttendanceTable(): string {
    if (this.attendanceRankings.length === 0) {
      return `
        <div class="card-glass" style="text-align: center; padding: 3rem 1rem;">
          <p style="color: var(--text-muted); margin: 0;">No hay registros de asistencia en la comunidad.</p>
        </div>
      `;
    }

    return `
      <div class="card-glass" style="overflow-x: auto; padding: 1.5rem;">
        <h3 style="font-size: 1.25rem; margin-bottom: 1.25rem; font-weight: 700; display:flex; align-items:center; gap:0.6rem;">
          <span style="color:var(--tyris-energy);">${getTyrisIcon('rankings', { size: 22 })}</span> Tabla de Honor: Consistencia de Asistencia
        </h3>
        <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.95rem;">
          <thead>
            <tr style="border-bottom: 1px solid var(--border-metallic); background: rgba(255,255,255,0.02);">
              <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; width: 80px;">Rango</th>
              <th style="padding: 1rem; color: var(--text-muted); font-weight: 600;">Atleta</th>
              <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Total Check-ins</th>
            </tr>
          </thead>
          <tbody>
            ${this.attendanceRankings.map((item, idx) => {
              const rankLabel = idx === 0
                ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#E5C158;color:#0F1218;font-weight:800;font-size:0.85rem;">1</span>`
                : idx === 1
                ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#A8A8A8;color:#0F1218;font-weight:800;font-size:0.85rem;">2</span>`
                : idx === 2
                ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#C08A54;color:#0F1218;font-weight:800;font-size:0.85rem;">3</span>`
                : `#${idx + 1}`;
              const highlightRow = idx < 3 ? 'background-color: rgba(111, 190, 149, 0.04); font-weight: 600;' : '';
              return `
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.03); ${highlightRow}">
                  <td style="padding: 0.9rem 1rem; font-size: 1.1rem; font-weight: 700;">${rankLabel}</td>
                  <td style="padding: 0.9rem 1rem;">
                    <div style="font-weight: 600;">${item.firstName} ${item.lastName}</div>
                    <div style="font-size: 0.75rem; color: var(--text-muted);">${item.email}</div>
                  </td>
                  <td style="padding: 0.9rem 1rem; text-align: center; color: var(--tyris-energy); font-size: 1.05rem; font-weight: 700;">
                    ${item.attendanceCount} días
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderPrsContent(): string {
    return `
      <!-- Selector de ejercicio -->
      <div class="card-glass" style="margin-bottom: 1.5rem; padding: 1.25rem;">
        <label for="select-exercise-ranking" style="display: block; font-size: 0.85rem; font-weight: 600; color: var(--text-muted); margin-bottom: 0.5rem;">
          Selecciona un Ejercicio para ver los mejores levantamientos:
        </label>
        <select 
          id="select-exercise-ranking" 
          class="form-control" 
          style="background-color: var(--bg-surface); border: 1px solid var(--border-metallic); color: var(--text-main); padding: 0.6rem; border-radius: var(--radius-sm); width: 100%; font-size: 0.95rem; outline: none;"
        >
          ${this.exercises.map(ex => `
            <option value="${ex.id}" ${this.selectedExerciseId === ex.id ? 'selected' : ''}>
              ${ex.name}
            </option>
          `).join('')}
        </select>
      </div>

      <!-- Tabla de Clasificación de PRs -->
      <div class="card-glass" style="overflow-x: auto; padding: 1.5rem;">
        <h3 style="font-size: 1.25rem; margin-bottom: 1.25rem; font-weight: 700; display:flex; align-items:center; gap:0.6rem;">
          <span style="color:var(--tyris-energy);">${getTyrisIcon('mis-records', { size: 22 })}</span> Tabla de Fuerza: 1 Rep Max Estimado
        </h3>
        
        ${this.prRankings.length === 0 ? `
          <div style="text-align: center; padding: 2rem 0;">
            <p style="color: var(--text-muted); margin: 0;">Nadie ha registrado un récord personal para este ejercicio todavía.</p>
          </div>
        ` : `
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.95rem;">
            <thead>
              <tr style="border-bottom: 1px solid var(--border-metallic); background: rgba(255,255,255,0.02);">
                <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; width: 80px;">Rango</th>
                <th style="padding: 1rem; color: var(--text-muted); font-weight: 600;">Atleta</th>
                <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Peso Máximo</th>
                <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Reps</th>
                <th style="padding: 1rem; color: var(--text-muted); font-weight: 600; text-align: right;">Fecha</th>
              </tr>
            </thead>
            <tbody>
              ${this.prRankings.map((item, idx) => {
                const rankLabel = idx === 0
                  ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#E5C158;color:#0F1218;font-weight:800;font-size:0.85rem;">1</span>`
                  : idx === 1
                  ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#A8A8A8;color:#0F1218;font-weight:800;font-size:0.85rem;">2</span>`
                  : idx === 2
                  ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:#C08A54;color:#0F1218;font-weight:800;font-size:0.85rem;">3</span>`
                  : `#${idx + 1}`;
                const highlightRow = idx < 3 ? 'background-color: rgba(111, 190, 149, 0.04); font-weight: 600;' : '';
                const dateStr = new Date(item.date).toLocaleDateString('es-ES', { timeZone: 'UTC' });
                return `
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.03); ${highlightRow}">
                    <td style="padding: 0.9rem 1rem; font-size: 1.1rem; font-weight: 700;">${rankLabel}</td>
                    <td style="padding: 0.9rem 1rem;">
                      <div style="font-weight: 600;">${item.student.firstName} ${item.student.lastName}</div>
                      <div style="font-size: 0.75rem; color: var(--text-muted);">${item.student.email}</div>
                    </td>
                    <td style="padding: 0.9rem 1rem; text-align: center; color: var(--tyris-energy); font-size: 1.05rem; font-weight: 700;">
                      ${item.weight} kg
                    </td>
                    <td style="padding: 0.9rem 1rem; text-align: center; font-weight: 600;">
                      x${item.reps}
                    </td>
                    <td style="padding: 0.9rem 1rem; text-align: right; color: var(--text-muted); font-size: 0.85rem;">
                      ${dateStr}
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        `}
      </div>
    `;
  }
}
